import { useEffect, useState } from 'react';
import { useStore, Position, useEdges } from 'reactflow';
import { Box } from '@mui/material';
import PPPoEIcon from '@shared/static/nodes/PPPoEIcon';
import { NODE_DEFAULT_PORTS, NODE_SIZES } from '@pages/schemes/constants';
import CustomMovableHandle from '@pages/schemes/components/Flow/CustomMovableHandle';
import useDynamicHandlePosition from '@pages/schemes/hooks/useDynamicHandlePosition';
import useSelectionStyle from '@pages/schemes/hooks/useSelectionStyle';
import useRotation from '@pages/schemes/hooks/useRotation';
import NodeToolbar from '@pages/schemes/components/Flow/NodeToolbar';
import HiddenNodeNameInput from '@pages/schemes/components/Flow/nodes/HiddenNodeNameInput';

const HANDLES_POOL = ['port0', 'port1'];

const PPPoENode = (props) => {
    const { id, data, selected, isConnectable } = props;
    const isNodeDraggable = useStore((state) => state.nodesDraggable);
    // eslint-disable-next-line no-unused-vars
    const [_rotateControlRef, rotation] = useRotation(data, id);
    const selectionStyle = useSelectionStyle(selected);
    const [selectedHandleId, setSelectedHandleId] = useState(data.selectedHandleId);
    const [handle_0_Position, setInitialHandle_0_Position] = useDynamicHandlePosition(
        rotation,
        data,
        HANDLES_POOL[0],
        Position.Top,
    );
    const [handle_1_Position, setInitialHandle_1_Position] = useDynamicHandlePosition(
        rotation,
        data,
        HANDLES_POOL[1],
        Position.Right,
    );

    const mapHandleIdToUseDynamicHandlePosition = {
        [HANDLES_POOL[0]]: [handle_0_Position, setInitialHandle_0_Position],
        [HANDLES_POOL[1]]: [handle_1_Position, setInitialHandle_1_Position],
    };

    const edges = useEdges();

    const nodeIsConnected = edges.some((edge) => edge.source === id && edge.sourceHandle === HANDLES_POOL[1]);

    useEffect(() => {
        if (!nodeIsConnected) {
            data.ports[1].ips = NODE_DEFAULT_PORTS.pppoeserver[1].ips;
        }
    }, [data, nodeIsConnected]);

    const handleUpdateIpsPoolSize = (newValue) => {
        data['ips-pool-size'] = newValue;
    };

    const handleUpdateAuthType = (newValue) => {
        data['auth-type'] = newValue;
    };

    return (
        <Box
            sx={{
                ...selectionStyle,
                maxWidth: NODE_SIZES['pppoeserver'].width,
                maxHeight: NODE_SIZES['pppoeserver'].height,
                transform: `rotate(${rotation}deg)`,
                padding: '2px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
            }}
        >
            <PPPoEIcon />
            <CustomMovableHandle
                nodeType="pppoeserver"
                nodeId={id}
                nodeSelected={selected}
                selectedHandleId={selectedHandleId}
                setSelectedHandleId={setSelectedHandleId}
                handleId={HANDLES_POOL[0]}
                position={mapHandleIdToUseDynamicHandlePosition[HANDLES_POOL[0]][0]}
                setInitialHandlePosition={mapHandleIdToUseDynamicHandlePosition[HANDLES_POOL[0]][1]}
                isConnectable={isConnectable}
                data={data}
                label={HANDLES_POOL[0]}
                movable={isNodeDraggable}
            />
            <CustomMovableHandle
                nodeType="pppoeserver"
                nodeId={id}
                nodeSelected={selected}
                selectedHandleId={selectedHandleId}
                setSelectedHandleId={setSelectedHandleId}
                handleId={HANDLES_POOL[1]}
                position={mapHandleIdToUseDynamicHandlePosition[HANDLES_POOL[1]][0]}
                setInitialHandlePosition={mapHandleIdToUseDynamicHandlePosition[HANDLES_POOL[1]][1]}
                isConnectable={isConnectable}
                data={data}
                label={HANDLES_POOL[1]}
                movable={isNodeDraggable}
            />
            {selected && (
                <NodeToolbar
                    HANDLES_POOL={HANDLES_POOL}
                    hidden={!selected}
                    nodeId={id}
                    NodeConfigMenuProps={{
                        withStaticPorts: true,
                        configureOnly: !isNodeDraggable,
                        ipsReadonly: !nodeIsConnected,
                        nodesParamsCustomisable: true,
                        handleUpdateIpsPoolSize,
                        handleUpdateAuthType,
                    }}
                />
            )}
            <HiddenNodeNameInput nodeData={data} nodeId={id} nodeSelected={selected} />
        </Box>
    );
};

export default PPPoENode;
